/**
 * Spoken structure: turning "new bullet" into an actual bullet (2026-09-30).
 *
 * ─── WHY THIS IS CLIENT-SIDE AND NOT IN THE PROMPT ──────────────────────────
 * The Haiku cleanup pass runs in the `transcribe` Edge Function, which the
 * ON-DEVICE engine never reaches — `startLocalDictation` hands its text
 * straight to the field. So anything built only in that function silently does
 * not apply on a job site with no signal, which is the case the fallback
 * exists for. Dictation would format one way at a desk and another way in a
 * half-built kitchen, with nothing saying why.
 *
 * These commands are therefore deterministic and local: same words, same
 * result, both engines, online or off, no model and no network. The model's
 * job (see `tidyDictation`) is the part a rule genuinely can't do — reading a
 * run-on sentence and finding the list inside it.
 *
 * ─── WHY A FIXED LIST OF PHRASES ────────────────────────────────────────────
 * Every phrase here is a phrase the designer can no longer dictate literally.
 * That is a real cost, so the list is short, each entry earns its place, and
 * none of them is a word that shows up in ordinary note prose: nobody writes
 * "new bullet" in a punch item. "Period" and "comma" are deliberately NOT
 * here — they appear constantly in real sentences ("a period of two weeks"),
 * and Whisper already punctuates.
 */

/** A bullet marker. One character, so `insertDictation`'s spacing rules hold. */
export const BULLET = '•';

export interface FormatState {
  /** The next number to use, once a numbered list has been started. */
  nextNumber: number;
  /** Whether the last thing emitted opened a list item. */
  inList: boolean;
}

export const INITIAL_FORMAT_STATE: FormatState = { nextNumber: 1, inList: false };

/**
 * How many times `applySpokenFormat` re-scans a chunk. Every retry or restart
 * declares what bounds it (CLAUDE.md): each pass must replace something or the
 * loop stops, and a 30-second clip cannot hold enough back-to-back commands to
 * reach this.
 */
const MAX_FORMAT_PASSES = 8;

export interface FormattedChunk {
  text: string;
  state: FormatState;
}

/**
 * Commands, longest first so "new paragraph" is not eaten by "new".
 *
 * Each is matched case-insensitively, optionally followed by a comma or full
 * stop that the transcriber added — "new bullet," and "New bullet." are the
 * same instruction, and a designer saying it naturally will produce both.
 */
type Command = 'bullet' | 'number' | 'line' | 'paragraph';

const COMMANDS: Array<{ phrases: string[]; kind: Command }> = [
  { kind: 'paragraph', phrases: ['new paragraph', 'next paragraph'] },
  { kind: 'bullet', phrases: ['new bullet', 'next bullet', 'bullet point'] },
  { kind: 'number', phrases: ['new number', 'next number', 'numbered item'] },
  { kind: 'line', phrases: ['new line', 'line break'] },
];
// Deliberately NOT commands, each one tried and removed: "new point" / "next
// point" ("a new point of sale terminal") and "next item" ("the next item we
// need to order") are ordinary things a designer says about a job. A command
// that eats real dictation is worse than one nobody discovers — the words
// vanish and nothing says they were taken.

/** `new bullet` → /(^|\s)new bullet[,.]?(\s|$)/i, built once. */
const PATTERN = (() => {
  const all = COMMANDS.flatMap((c) => c.phrases)
    .sort((a, b) => b.length - a.length)
    .map((p) => p.replace(/\s+/g, '\\s+'));
  // Order matters, and getting it wrong is subtle. The boundary is ASSERTED
  // first (`(?=…)`), and only then is the trailing space consumed:
  //
  //   `(?:[ \t]*|(?=…))`  — the empty match wins everywhere, so "the new
  //                          bulletin board" became "the\n• in board".
  //   `(?=…)[ \t]*`       — must be at a word boundary, THEN eat the space.
  //
  // The space has to be eaten, or every bullet renders indented by one.
  return new RegExp(
    `(^|[\\s"'(\\[])(${all.join('|')})[,.!?]?(?=$|[\\s"')\\]])[ \\t]*`, 'gi');
})();

function kindOf(phrase: string): Command {
  const norm = phrase.toLowerCase().replace(/\s+/g, ' ').trim();
  return COMMANDS.find((c) => c.phrases.includes(norm))?.kind ?? 'line';
}

/**
 * Replace spoken commands in one chunk of dictation with real structure.
 *
 * `state` carries across chunks because dictation arrives in separate clips
 * (first ~5s, then 30s): "number one… number two" spans a clip boundary, and
 * a counter that reset there would produce two number ones. Pass the returned
 * state into the next call.
 */
export function applySpokenFormat(
  chunk: string, state: FormatState = INITIAL_FORMAT_STATE,
): FormattedChunk {
  let next = { ...state };
  const replaceOnce = (input: string): string => input.replace(PATTERN, (_m, lead: string, phrase: string) => {
    // A leading SPACE is swallowed by the break that replaces it; a leading
    // quote or bracket is the designer's own punctuation and has to survive.
    const keep = /^\s*$/.test(lead ?? '') ? '' : lead;
    switch (kindOf(phrase)) {
      case 'paragraph':
        next = { ...next, inList: false };
        return `${keep}\n\n`;
      case 'bullet':
        next = { ...next, inList: true };
        return `${keep}\n${BULLET} `;
      case 'number': {
        const n = next.nextNumber;
        next = { nextNumber: n + 1, inList: true };
        return `${keep}\n${n}. `;
      }
      case 'line':
      default:
        return `${keep}\n`;
    }
  });

  // Run until stable, because a match CONSUMES the whitespace in front of it
  // and the next command needs that whitespace to match at all: "new paragraph
  // new paragraph" replaced only the first, leaving the second as literal
  // text. Bounded, so a pathological input cannot spin: each pass either
  // replaces something or we stop.
  let out = chunk;
  for (let pass = 0; pass < MAX_FORMAT_PASSES; pass++) {
    const after = replaceOnce(out);
    if (after === out) break;
    out = after;
  }
  return { text: tidyWhitespace(out), state: next };
}

/**
 * Collapse the whitespace a replacement leaves behind.
 *
 * "regrout the niche , new bullet replace…" becomes "…niche\n• replace…": the
 * space before the break and after the marker both have to go, or every bullet
 * renders indented by one.
 */
export function tidyWhitespace(s: string): string {
  return s
    // Spaces/tabs around a newline we inserted.
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    // A list marker must be followed by exactly one space, whatever the
    // transcriber left behind.
    .replace(/^([ \t]*(?:•|\d+[.)]))[ \t]{2,}/gm, '$1 ')
    // Never more than one blank line.
    .replace(/\n{3,}/g, '\n\n')
    // Leading/trailing space on each line, but keep the lines.
    .split('\n').map((l) => l.replace(/[ \t]+$/, '')).join('\n');
}

/**
 * Whether a chunk contained any command at all.
 *
 * Used to decide if the model's tidy pass should run: a designer who dictated
 * their own structure has said what they want, and rewriting it would be the
 * app overruling them.
 */
export function hasSpokenFormat(chunk: string): boolean {
  PATTERN.lastIndex = 0;
  return PATTERN.test(chunk);
}

/**
 * The number a numbered list should continue from, read off existing text.
 *
 * The field may already hold a list the designer typed, or one dictated before
 * a reload. Starting again at 1 underneath "3." is the kind of thing nobody
 * notices until a contractor is reading it.
 */
export function continueNumbering(existing: string): FormatState {
  const matches = [...existing.matchAll(/^\s*(\d+)[.)]\s/gm)];
  const last = matches.at(-1);
  if (!last) return { ...INITIAL_FORMAT_STATE };
  return { nextNumber: Number(last[1]) + 1, inList: true };
}

/**
 * The hint shown while dictating, so the commands are discoverable at all.
 *
 * A feature nobody is told about is a feature nobody uses, and these are
 * words — there is nothing on screen to click that would reveal them. Shown
 * only where structure is possible: on a one-line field (a punch item) it
 * would be advertising something that field refuses to do.
 */
export function listeningHint(kind: 'line' | 'notes' | 'scope'): string {
  const base = 'Listening… tap the mic again to stop.';
  return kind === 'line' ? base : `${base} Say “new bullet” or “next number” to format as you go.`;
}
